
import { Component } from 'vue-property-decorator';
import AmountDisplayerFilterable from '@/components/reports-v3/components/filterables/AmountDisplayerFilterable.vue';
import moment from 'moment';
import { COL, sqlSum, sqlSumByDocType, buildExistingCustomerFilter, getTableName } from '@/worker/db/sqlHelpers';
import FilterWidget from '../shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import { addComma } from '@/util/number';
import gdbx from '@/store/modules/gdbx';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
	components: {
		AmountDisplayerFilterable,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class SalesExistingCustomerNumberDisplayer extends FilterWidget {
	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public customersCount = 0;
	public totalSales: any = '';
	public salesPerCustomer: any = '';

	public get permissionIds(): PermissionsGroup[] {
		return ['sales', 'customers'];
	}

	public get currentYear() {
		return moment(this.selectedAsOfDate).year();
	}

	public get currency() {
		return gdbx.currencySymbol;
	}

	public get decimal() {
		return gdbx.numDecimal;
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get expensiveHook() {
		const {
			selectedDateRange,
			selectedAsOfDate,
			selectedAgents,
			selectedStockItems,
			currentYear,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		} = this;
		return JSON.stringify([
			selectedDateRange,
			selectedAsOfDate,
			selectedAgents,
			selectedStockItems,
			currentYear,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.3);

		// Build filter clause for existing customers (firstDeal before date range start)
		const existingCustomerFilter = buildExistingCustomerFilter({
			beforeDate: this.selectedDateRange[0],
			agents: this.selectedAgents,
		});

		const queryHead = await this._ensureTablesLoaded(
			['sales', 'creditNotes', 'customerTrades', 'globalCustomers'] as any[],
		);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.9);

		const salesTable = getTableName('sales');
		const cnTable = getTableName('creditNotes');
		const arTable = getTableName('customerTrades');
		const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const allAgentLen = (gdbx.allAgents && gdbx.allAgents.length) || 0;
		const agentFilter = this.selectedAgents && this.selectedAgents.length > 0
			&& this.selectedAgents.length !== allAgentLen
			? ` AND ${COL.AGENT} IN (${this.selectedAgents.map((a: string) => `'${escapeStr(a)}'`).join(', ')})`
			: '';

		// Run all rawQuery in parallel with existing customer filter
		const [slResult, slCnResult, arResult] = await Promise.all([
			// SL: Get IV/CS amount, DN amount, customer count
			queryHead.rawQuery(`
				SELECT
					${sqlSumByDocType(['IV', 'CS'], COL.AMOUNT, 'iv_cs_amount')},
					${sqlSumByDocType(['DN'], COL.AMOUNT, 'dn_amount')},
					COUNT(DISTINCT ${COL.CODE}) as customer_count
				FROM ${salesTable}
				WHERE ${dateFilter}${agentFilter}${existingCustomerFilter}
			`),
			// SL Credit Notes: amount
			queryHead.rawQuery(`
				SELECT ${sqlSum(COL.AMOUNT, 'cn_amount')}
				FROM ${cnTable}
				WHERE ${dateFilter}${agentFilter}${existingCustomerFilter}
			`),
			// AR: Get IV, CN, DN amounts, customer count
			queryHead.rawQuery(`
				SELECT
					${sqlSumByDocType(['IV'], COL.LOCALAMOUNT, 'iv_amount')},
					${sqlSumByDocType(['CN'], COL.LOCALAMOUNT, 'cn_amount')},
					${sqlSumByDocType(['DN'], COL.LOCALAMOUNT, 'dn_amount')},
					COUNT(DISTINCT ${COL.CODE}) as customer_count
				FROM ${arTable}
				WHERE ${dateFilter}${agentFilter}${existingCustomerFilter}
			`),
		]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.95);

		// Extract values
		const slRow = slResult[0] || {};
		const slIvCsAmount = Number(slRow.iv_cs_amount) || 0;
		const slDnAmount = Number(slRow.dn_amount) || 0;
		const slCustomerCount = Number(slRow.customer_count) || 0;

		const slCnRow = slCnResult[0] || {};
		const slCnAmount = Number(slCnRow.cn_amount) || 0;

		const arRow = arResult[0] || {};
		const arIvAmount = Number(arRow.iv_amount) || 0;
		const arCnAmount = Number(arRow.cn_amount) || 0;
		const arDnAmount = Number(arRow.dn_amount) || 0;
		const arCustomerCount = Number(arRow.customer_count) || 0;

		let customersCount: number;
		let sales: number;

		if (this.slPhModuleOnly) {
			// SL Module calculations
			sales = slIvCsAmount
				- (this.includeCreditNote ? slCnAmount : 0)
				+ (this.includeDebitNote ? slDnAmount : 0);
			customersCount = slCustomerCount;
		} else {
			// AR Module calculations
			sales = arIvAmount
				- (this.includeCreditNote ? arCnAmount : 0)
				+ (this.includeDebitNote ? arDnAmount : 0);
			customersCount = arCustomerCount;
		}

		this.customersCount = customersCount;
		this.totalSales = addComma(sales, this.decimal, true);
		this.salesPerCustomer = addComma(
			customersCount !== 0 ? sales / customersCount : 0,
			this.decimal,
			true,
		);

		this.generateLoadingText(1);
		this.saveHistory('customersCount', 'totalSales', 'salesPerCustomer');
	}
}
